
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { NumberFormatting } from '@sageworks/core-logic';
import type { PlaidParsedAssetReport } from '@sageworks/jpi/src/auto-generated/model/plaidParsedAssetReport';
import { FeatureFlagging, UsableProducts } from '@sageworks/jpi';

const POLLING_INTERVAL = 3000;
const MAX_POLLING_AMOUNT = 120000; // 2 minutes

declare global {
	interface Window {
		Plaid: any;
	}
}

export default Vue.extend({
	name: 'PlaidComponent',
	components: {
		FontAwesomeIcon
	},
	props: {
		componentId: {
			type: String,
			required: true
		},
		addNewBtnLabel: {
			type: String,
			default: 'Add Bank Account(s)'
		},
		applicationId: {
			type: String
		},
		customerId: {
			type: Number
		},
	},
	data() {
		return {
			handler: null as any,
			errorMessage: '' as string,
			statusPollingIntervalId: null as number | null,
			statusPollingCounter: 0,
			statusPollingStarted: false,
			plaidFeatureFlagActive: false
		};
	},
	computed: {
		...mapGetters('Plaid', ['selectDataAssets', 'selectDocumentStatus', 'selectCredentials', 'selectLoadDataAssetsStatus', 'selectAddBankAccountsStatus']),
		...mapState('LoanApplicationData', ['loanApplication']),
		...mapState('Plaid', ['isPlaidEnabled']),
		...mapState('UsableProducts', ['usableProducts']),
		formattedDataAssets() {
			const dataAssets = this.selectDataAssets as Map<number, PlaidParsedAssetReport[]>;
			if (this.customerId && dataAssets) {
				return dataAssets.get(this.customerId)?.map((data: PlaidParsedAssetReport) => {
					return (
						{
							institution: data.institution,
							accountNumber: data.accountNumber,
							description: data.description,
							accountType: data.accountType,
							balance: NumberFormatting.formatAsCurrency(Number(data.balance), 2, true)
						}
					);
				}) ?? [];
			}

			return [];
		},
		loadingMessage() {
			return this.statusPollingStarted ? 'Processing Accounts' : '';
		},
		showComponent() {
			const hasPlaidSubscription = this.usableProducts.has(UsableProducts.ProductsEnum.Plaid);
			return this.isPlaidEnabled && hasPlaidSubscription && this.plaidFeatureFlagActive;
		}
	},
	watch: {
		customerId: {
			handler() {
				this.loadData();
			}
		},
		selectAddBankAccountsStatus: {
			handler(newValue: string) {
				if (newValue === 'Error') {
					this.errorMessage = 'Failed to add bank accounts';
					this.stopStatusPolling();
					this.directStore.dispatch.Plaid.resetPlaidStatus();
				}
			}
		},
	},
	async beforeMount() {
		this.directStore.dispatch.Plaid.getPlaidEnabled();
		this.directStore.dispatch.UsableProducts.fetchUsableProducts();
		this.plaidFeatureFlagActive = await this.directStore.dispatch.FeatureFlag.fetchIsFeatureFlagActive(FeatureFlagging.FeatureFlagEnum.EnablePlaidInDya);
	},
	mounted() {
		if (!window.Plaid) {
			let Plaid = document.createElement('script');
			Plaid.setAttribute('src', 'https://cdn.plaid.com/link/v2/stable/link-initialize.js');
			document.head.appendChild(Plaid);
		}
		this.directStore.dispatch.Plaid.resetPlaidStatus();
	},
	destroyed() {
		this.stopStatusPolling();
	},
	methods: {
		async loadData() {
			this.errorMessage = '',
				await this.directStore.dispatch.Plaid.loadDataAssets({
					applicationId: this.directStore.state.LoanApplicationData.loanApplication?.id,
					customerId: this.customerId
				});
		},
		async addBankAccounts() {
			this.errorMessage = '',
				await this.directStore.dispatch.Plaid.addBankAccounts({
					applicationId: this.directStore.state.LoanApplicationData.loanApplication?.id,
					customerId: this.customerId,
					beforeCreate: () => {
						this.statusPollingStarted = true;
					},
					created: this.startStatusPolling
				});

		},
		async checkDocumentStatus(mappingId: number) {
			this.errorMessage = '';
			if (this.selectDocumentStatus === 'Success') {
				this.stopStatusPolling();
				this.directStore.dispatch.Plaid.resetPlaidStatus();
				this.loadData();
				return;
			} else if (this.selectDocumentStatus === 'Error') {
				this.stopStatusPolling();
				this.directStore.dispatch.Plaid.resetPlaidStatus();
			} else {
				await this.directStore.dispatch.Plaid.getPlaidDocumentStatus({
					customerId: this.customerId,
					applicationId: this.directStore.state.LoanApplicationData.loanApplication?.id,
					mappingId,
				});
			}
		},
		async startStatusPolling(mappingId: number) {
			this.stopStatusPolling();
			this.statusPollingIntervalId = setInterval(async () => {
				await this.checkDocumentStatus(mappingId);
				this.statusPollingCounter++;

			}, POLLING_INTERVAL);

			setTimeout(() => {
				this.stopStatusPolling();
				this.loadData();
			}, MAX_POLLING_AMOUNT);
		},
		stopStatusPolling() {
			if (this.statusPollingIntervalId) {
				clearInterval(this.statusPollingIntervalId);
				this.statusPollingIntervalId = null;
				this.statusPollingStarted = false;
			}
		}
	}
});
